import Cover from '../cover/Cover';
import Fade from '../fade/Fade';
import Close from './close/Close';
import Panel from './panel/Panel';
import Title from './title/TItle';

const TWEEN = {
    DURATION: 250,
    EASE: {
        SHOW: Phaser.Math.Easing.Back.Out,
        HIDE: Phaser.Math.Easing.Back.In,
    },
};

export default class Popup extends Phaser.GameObjects.Container {
    private fade!: Fade;
    private mainContainer!: Phaser.GameObjects.Container;
    public contentContainer!: Phaser.GameObjects.Container;

    private title!: Title;
    private panel!: Panel;
    private close!: Close;
    private cover!: Cover;

    private shape!: Phaser.GameObjects.Rectangle;
    private contentMask!: Phaser.Display.Masks.BitmapMask;

    constructor(scene: Phaser.Scene, title: string, height: number) {
        super(scene, 0, 0);
        this.scene.layers.popups.add(this);

        this.setupFade();
        this.setupMainContainer();
        this.setupTitle(title);
        this.setupPanel(height);
        this.setupClose();
        this.setupCover();
        this.finalizePosition();
        this.setupMask(height);
        this.setupContentContainer(height);

        this.mainContainer.setScale(0);
    }

    private setupFade(): void {
        this.fade = new Fade(this.scene, 0, 0);
        this.fade.setOrigin(0);
        this.add(this.fade);
    }

    private setupMainContainer(): void {
        const x = this.scene.cameras.main.centerX;
        const y = this.scene.cameras.main.centerY;

        this.mainContainer = this.scene.add.container(x, y);
        this.add(this.mainContainer);
    }

    private setupTitle(title: string): void {
        const x = 0;
        const y = 0;

        this.title = new Title(this.scene, x, y, title);
        this.mainContainer.add(this.title);
    }

    private setupPanel(height: number): void {
        const x = 0;
        const y = this.title.totalHeight;

        this.panel = new Panel(this.scene, x, y, height);
        this.mainContainer.add(this.panel);
    }

    private setupClose(): void {
        const x = this.title.totalWidth / 2 - 45;
        const y = this.title.totalHeight / 2;

        this.close = new Close(this.scene, x, y);
        this.mainContainer.add(this.close);
    }

    private setupCover(): void {
        this.cover = new Cover(this.scene, 0, 0);
        this.cover.setOrigin(0);
        this.add(this.cover);
    }

    private finalizePosition(): void {
        for (const element of [this.title, this.panel, this.close]) {
            element.y -= (this.title.totalHeight + this.panel.totalHeight) / 2;
        }
    }

    private setupMask(height: number): void {
        const x = this.scene.cameras.main.centerX;
        const y = this.scene.cameras.main.centerY + this.title.totalHeight / 2;

        this.shape = this.scene.add.rectangle(x, y, this.title.totalWidth, height, 0xffffff, 1);
        this.shape.setVisible(false);

        this.contentMask = this.shape.createBitmapMask();

        this.shape.setScale(0);
    }

    private setupContentContainer(height: number): void {
        const x = 0;
        const y = -height / 2 + this.title.totalHeight / 2;

        this.contentContainer = this.scene.add.container(x, y);
        this.contentContainer.setMask(this.contentMask);
        this.mainContainer.add(this.contentContainer);
    }

    public async show(): Promise<void> {
        this.cover.show();
        await this.fade.show();

        return new Promise((resolve) => {
            this.scene.tweens.add({
                targets: [this.mainContainer, this.shape],
                duration: TWEEN.DURATION,
                ease: TWEEN.EASE.SHOW,
                scaleX: 1,
                scaleY: 1,
                onComplete: () => {
                    this.cover.hide();
                    this.close.once('click', this.hide, this);
                    resolve();
                },
            });
        });
    }

    public async hide(): Promise<void> {
        this.cover.show();
        this.close.off('click', this.hide, this);

        return new Promise((resolve) => {
            this.scene.tweens.add({
                targets: [this.mainContainer, this.shape],
                duration: TWEEN.DURATION,
                ease: TWEEN.EASE.HIDE,
                scaleX: 0,
                scaleY: 0,
                onComplete: async () => {
                    await this.fade.hide();
                    this.cover.hide();
                    resolve();
                },
            });
        });
    }

    destroy(fromScene?: boolean): void {
        this.shape.destroy();
        this.contentMask.destroy();
        super.destroy(fromScene);
    }
}
